import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Footer from '../components/Footer';
import PostIllustration from '../components/PostIllustration';

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  tags: string[];
  image_url?: string;
  published_at: string;
}

const FONT = '"Plus Jakarta Sans", system-ui, sans-serif';

// Color swatches per tag — claude.com/blog style
const TAG_COLORS: Record<string, { bg: string; text: string }> = {
  'FinOps':              { bg: 'rgba(232,184,76,0.18)',  text: '#3a5c00' },
  'Cost Optimization':   { bg: 'rgba(15,61,46,0.1)',    text: '#0F3D2E' },
  'AWS':                 { bg: 'rgba(255,153,0,0.12)',    text: '#a05e00' },
  'EC2':                 { bg: 'rgba(6,182,212,0.12)',    text: '#0e6578' },
  'S3':                  { bg: 'rgba(168,85,247,0.12)',   text: '#6b21a8' },
  'RDS':                 { bg: 'rgba(239,68,68,0.12)',    text: '#991b1b' },
  'Reserved Instances':  { bg: 'rgba(34,197,94,0.12)',    text: '#14532d' },
  'Savings Plans':       { bg: 'rgba(234,179,8,0.12)',    text: '#713f12' },
  'Kubernetes':          { bg: 'rgba(59,130,246,0.12)',   text: '#1e3a8a' },
  'Serverless':          { bg: 'rgba(249,115,22,0.12)',   text: '#7c2d12' },
  'EKS':                 { bg: 'rgba(16,185,129,0.12)',   text: '#064e3b' },
  'Lambda':              { bg: 'rgba(139,92,246,0.12)',   text: '#4c1d95' },
};

function tagStyle(tag: string) {
  return TAG_COLORS[tag] ?? { bg: 'rgba(107,114,128,0.12)', text: '#374151' };
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function Blog() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTag, setActiveTag] = useState<string>('All');
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  useEffect(() => {
    axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/blog`)
      .then(r => setPosts(r.data))
      .finally(() => setLoading(false));
  }, []);

  const allTags = ['All', ...Array.from(new Set(posts.flatMap(p => p.tags)))];
  const filtered = activeTag === 'All' ? posts : posts.filter(p => p.tags.includes(activeTag));
  const [featured, ...rest] = filtered;

  return (
    <div className="min-h-screen" style={{ background: '#F6F1E3', fontFamily: FONT }}>

      {/* Header */}
      <div className="relative overflow-hidden py-20 px-4" style={{ background: 'rgb(7,40,29)' }}>
        {/* Glow orbs */}
        <div className="absolute inset-0 pointer-events-none">
          <div style={{ position: 'absolute', top: '-80px', right: '-60px', width: '480px', height: '480px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(232,184,76,0.12) 0%, transparent 70%)' }} />
          <div style={{ position: 'absolute', bottom: '-100px', left: '-80px', width: '500px', height: '500px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(78,125,94,0.18) 0%, transparent 70%)' }} />
          <div style={{ position: 'absolute', top: '40%', left: '40%', width: '300px', height: '300px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(15,61,46,0.25) 0%, transparent 70%)' }} />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: '#E8B84C' }}>
            CloudFrugal Blog
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight" style={{ letterSpacing: '-0.02em' }}>
            Cloud cost insights &amp; FinOps strategies
          </h1>
          <p className="text-lg max-w-xl mx-auto" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Practical tips to reduce your AWS bill, optimize resources, and build a cost-aware culture.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-12">

        {/* Tag filters */}
        {!loading && allTags.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-12">
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => setActiveTag(tag)}
                className="px-4 py-1.5 rounded-full text-sm font-semibold transition-all"
                style={
                  activeTag === tag
                    ? { background: '#0F3D2E', color: '#fff' }
                    : { background: '#fff', color: '#374151', border: '1px solid rgba(15,61,46,0.15)' }
                }
              >
                {tag}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse" style={{ border: '1px solid rgba(15,61,46,0.08)' }}>
                <div className="h-44 bg-gray-100" />
                <div className="p-5">
                  <div className="h-3 bg-gray-100 rounded w-1/4 mb-3" />
                  <div className="h-5 bg-gray-100 rounded w-3/4 mb-2" />
                  <div className="h-4 bg-gray-100 rounded w-full mb-1" />
                  <div className="h-4 bg-gray-100 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-lg font-medium" style={{ color: 'rgba(15,22,32,0.4)' }}>No posts yet. Check back soon!</p>
          </div>
        ) : (
          <div
            onMouseLeave={() => setHoveredId(null)}
          >
            {/* Featured post — first card, full width */}
            {featured && (
              <Link
                to={`/blog/${featured.slug}`}
                onMouseEnter={() => setHoveredId(featured.id)}
                className="group flex flex-col sm:flex-row gap-0 bg-white rounded-2xl overflow-hidden mb-8 transition-all duration-300"
                style={{
                  border: '1px solid rgba(15,61,46,0.1)',
                  opacity: hoveredId !== null && hoveredId !== featured.id ? 0.35 : 1,
                  transition: 'opacity 0.25s ease, box-shadow 0.25s ease',
                  boxShadow: hoveredId === featured.id ? '0 20px 40px rgba(7,40,29,0.12)' : 'none',
                }}
              >
                <PostIllustration tags={featured.tags} title={featured.title}
                  className="sm:w-1/2 h-56 sm:h-auto flex-shrink-0 object-cover"
                  style={{ minHeight: '14rem' }}
                />
                <div className="p-7 flex flex-col justify-center">
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {featured.tags.slice(0, 2).map(tag => {
                      const s = tagStyle(tag);
                      return (
                        <span key={tag} className="text-xs font-semibold px-2.5 py-0.5 rounded-full" style={{ background: s.bg, color: s.text }}>
                          {tag}
                        </span>
                      );
                    })}
                    <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full" style={{ background: 'rgba(232,184,76,0.2)', color: '#3a5c00' }}>
                      Featured
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold mb-3 leading-snug" style={{ color: 'rgb(22,30,45)', letterSpacing: '-0.01em' }}>
                    {featured.title}
                  </h2>
                  {featured.excerpt && (
                    <p className="text-sm leading-relaxed mb-5 line-clamp-3" style={{ color: 'rgba(15,22,32,0.55)' }}>{featured.excerpt}</p>
                  )}
                  <p className="text-xs" style={{ color: 'rgba(15,22,32,0.35)' }}>{formatDate(featured.published_at)}</p>
                </div>
              </Link>
            )}

            {/* Rest of posts — 3-col grid */}
            {rest.length > 0 && (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {rest.map(post => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    onMouseEnter={() => setHoveredId(post.id)}
                    className="group bg-white rounded-2xl overflow-hidden flex flex-col"
                    style={{
                      border: '1px solid rgba(15,61,46,0.1)',
                      opacity: hoveredId !== null && hoveredId !== post.id ? 0.35 : 1,
                      transition: 'opacity 0.25s ease, box-shadow 0.25s ease',
                      boxShadow: hoveredId === post.id ? '0 20px 40px rgba(7,40,29,0.12)' : 'none',
                    }}
                  >
                    <PostIllustration tags={post.tags} title={post.title} className="w-full h-44" />
                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {post.tags.slice(0, 2).map(tag => {
                          const s = tagStyle(tag);
                          return (
                            <span key={tag} className="text-xs font-semibold px-2.5 py-0.5 rounded-full" style={{ background: s.bg, color: s.text }}>
                              {tag}
                            </span>
                          );
                        })}
                      </div>
                      <h2 className="text-base font-bold mb-2 line-clamp-2 flex-1" style={{ color: 'rgb(22,30,45)', letterSpacing: '-0.01em' }}>
                        {post.title}
                      </h2>
                      {post.excerpt && (
                        <p className="text-sm line-clamp-2 mb-4" style={{ color: 'rgba(15,22,32,0.5)' }}>{post.excerpt}</p>
                      )}
                      <p className="text-xs mt-auto" style={{ color: 'rgba(15,22,32,0.35)' }}>{formatDate(post.published_at)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
